import { AuthService } from './auth'

const BASE_URL = 'https://api.quali-d.com'

function authHeaders () {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${AuthService.getAuthToken()}`
  }
}

async function handleResponse (res) {
  const data = await res.json()
  if (!res.ok || data.success === false) {
    throw new Error(data.message || `HTTP ${res.status}`)
  }
  return data
}

export class IVRService {
  // ── Flows ────────────────────────────────────────────────────────────────
  static async getFlows () {
    return handleResponse(await fetch(`${BASE_URL}/ivr/flows`, { headers: authHeaders() }))
  }

  static async createFlow (payload) {
    return handleResponse(await fetch(`${BASE_URL}/ivr/flows`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(payload)
    }))
  }

  static async getFlow (id) {
    return handleResponse(await fetch(`${BASE_URL}/ivr/flows/${id}`, { headers: authHeaders() }))
  }

  static async updateFlow (id, payload) {
    return handleResponse(await fetch(`${BASE_URL}/ivr/flows/${id}`, {
      method: 'PUT',
      headers: authHeaders(),
      body: JSON.stringify(payload)
    }))
  }

  static async deleteFlow (id) {
    return handleResponse(await fetch(`${BASE_URL}/ivr/flows/${id}`, {
      method: 'DELETE',
      headers: authHeaders()
    }))
  }

  static async duplicateFlow (id) {
    return handleResponse(await fetch(`${BASE_URL}/ivr/flows/${id}/duplicate`, {
      method: 'POST',
      headers: authHeaders()
    }))
  }

  static async activateFlow (id) {
    return handleResponse(await fetch(`${BASE_URL}/ivr/flows/${id}/activate`, {
      method: 'POST',
      headers: authHeaders()
    }))
  }

  static async deactivateFlow (id) {
    return handleResponse(await fetch(`${BASE_URL}/ivr/flows/${id}/deactivate`, {
      method: 'POST',
      headers: authHeaders()
    }))
  }

  // ── Audio Library ─────────────────────────────────────────────────────────
  static async getAudio () {
    return handleResponse(await fetch(`${BASE_URL}/ivr/audio`, { headers: authHeaders() }))
  }

  static async generateTTS (payload) {
    return handleResponse(await fetch(`${BASE_URL}/ivr/audio/tts`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(payload)
    }))
  }

  static async uploadAudio (formData) {
    const token = AuthService.getAuthToken()
    return handleResponse(await fetch(`${BASE_URL}/ivr/audio/upload`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: formData
    }))
  }

  static async getAudioBlobUrl (id) {
    const res = await fetch(`${BASE_URL}/ivr/audio/${id}/url`, { headers: authHeaders() })
    if (!res.ok) throw new Error(`Audio fetch failed (${res.status})`)
    const blob = await res.blob()
    return URL.createObjectURL(blob)
  }

  static async deleteAudio (id) {
    return handleResponse(await fetch(`${BASE_URL}/ivr/audio/${id}`, {
      method: 'DELETE',
      headers: authHeaders()
    }))
  }

  // ── Call Sessions ─────────────────────────────────────────────────────────
  static async getSessions (params = {}) {
    const qs = new URLSearchParams(params).toString()
    const url = qs ? `${BASE_URL}/ivr/sessions?${qs}` : `${BASE_URL}/ivr/sessions`
    return handleResponse(await fetch(url, { headers: authHeaders() }))
  }

  static async getSession (id) {
    return handleResponse(await fetch(`${BASE_URL}/ivr/sessions/${id}`, { headers: authHeaders() }))
  }

  // ── Blacklist ─────────────────────────────────────────────────────────────
  static async getBlacklist () {
    return handleResponse(await fetch(`${BASE_URL}/ivr/blacklist`, { headers: authHeaders() }))
  }

  static async addToBlacklist (payload) {
    return handleResponse(await fetch(`${BASE_URL}/ivr/blacklist`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(payload)
    }))
  }

  static async removeFromBlacklist (id) {
    return handleResponse(await fetch(`${BASE_URL}/ivr/blacklist/${id}`, {
      method: 'DELETE',
      headers: authHeaders()
    }))
  }

  // ── Voicemails ────────────────────────────────────────────────────────────
  static async getVoicemails (params = {}) {
    const qs = new URLSearchParams(params).toString()
    const url = qs ? `${BASE_URL}/ivr/voicemails?${qs}` : `${BASE_URL}/ivr/voicemails`
    return handleResponse(await fetch(url, { headers: authHeaders() }))
  }

  static async getVoicemail (id) {
    return handleResponse(await fetch(`${BASE_URL}/ivr/voicemails/${id}`, { headers: authHeaders() }))
  }

  static async markVoicemailRead (id) {
    return handleResponse(await fetch(`${BASE_URL}/ivr/voicemails/${id}/read`, {
      method: 'POST',
      headers: authHeaders()
    }))
  }

  static async deleteVoicemail (id) {
    return handleResponse(await fetch(`${BASE_URL}/ivr/voicemails/${id}`, {
      method: 'DELETE',
      headers: authHeaders()
    }))
  }

  // ── Dashboard Stats ───────────────────────────────────────────────────────
  static async getStats () {
    return handleResponse(await fetch(`${BASE_URL}/ivr/stats`, { headers: authHeaders() }))
  }

  // ── Connection Status ─────────────────────────────────────────────────────
  static async getConnectionStatus () {
    return handleResponse(await fetch(`${BASE_URL}/ivr/connection-status`, { headers: authHeaders() }))
  }

  // ── Extensions ────────────────────────────────────────────────────────────
  static async getExtensions () {
    return handleResponse(await fetch(`${BASE_URL}/extensions/`, { headers: authHeaders() }))
  }

  // ── CDR (Call Detail Records) ─────────────────────────────────────────────
  static async getCdrHistory (params = {}) {
    const qs = new URLSearchParams(params).toString()
    const url = qs ? `${BASE_URL}/cdr?${qs}` : `${BASE_URL}/cdr`
    return handleResponse(await fetch(url, { headers: authHeaders() }))
  }

  // ── Agents + Extensions (unified) ────────────────────────────────────────
  static async getAgentsExtensions () {
    return handleResponse(await fetch(`${BASE_URL}/agents/extensions`, { headers: authHeaders() }))
  }

  // ── Agent ↔ Extension Connect ─────────────────────────────────────────────
  static async connectExtension (payload) {
    return handleResponse(await fetch(`${BASE_URL}/agent/connect-extension`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(payload)
    }))
  }

  // ── Queues ────────────────────────────────────────────────────────────────
  static async getQueues () {
    return handleResponse(await fetch(`${BASE_URL}/ivr/queues`, { headers: authHeaders() }))
  }

  static async createQueue (payload) {
    return handleResponse(await fetch(`${BASE_URL}/ivr/queues`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(payload)
    }))
  }

  static async updateQueue (id, payload) {
    return handleResponse(await fetch(`${BASE_URL}/ivr/queues/${id}`, {
      method: 'PUT',
      headers: authHeaders(),
      body: JSON.stringify(payload)
    }))
  }

  static async deleteQueue (id) {
    return handleResponse(await fetch(`${BASE_URL}/ivr/queues/${id}`, {
      method: 'DELETE',
      headers: authHeaders()
    }))
  }

  static async addQueueMember (id, payload) {
    return handleResponse(await fetch(`${BASE_URL}/ivr/queues/${id}/members`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(payload)
    }))
  }

  static async removeQueueMember (id, extension) {
    return handleResponse(await fetch(`${BASE_URL}/ivr/queues/${id}/members/${encodeURIComponent(extension)}`, {
      method: 'DELETE',
      headers: authHeaders()
    }))
  }

  // ── Queue Menu Builder ────────────────────────────────────────────────────
  static async createQueueMenu (payload) {
    return handleResponse(await fetch(`${BASE_URL}/ivr/queue-menu`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(payload)
    }))
  }
}
